import { useMemo } from 'react';
import useResourceLoader from '../../../util/useResourceLoader';
import fetchApi, { FetchOptions } from '../../../util/fetchApi';
import checkErrors from '../../../api/checkErrors';
import Jnx, { JnxExpr, MultipleJnxObject, useJnx, useMultipleJnx } from '../../../util/jnx';
import { JSONObject } from '../../../api/types';
import { APIString, computeParamString, InputLookupParams } from './useOptionsLookup';


/**
 * Represents a lookup configuration object for counting options.
 * 
 * @property api - The API identifier or URL string to fetch the data from.
 * @property entity - Optional entity name, which can alter the API path for entity-specific data.
 * @property jnx - A Jnx expression used to transform or select data from the API response.
 * @property params - Parameters used to customize the lookup query.
 * @property options - Additional fetch options for the API request.
 */
export type LookupCountObject = {
  api: APIString,
  entity?: string | null,
  jnx: JnxExpr<any[]>,
  params: InputLookupParams,
  options: FetchOptions,
};


/**
 * React hook that fetches and returns the count of options based on the provided lookup configuration.
 * It uses the root form data and a path string to compute the query parameters and fetch the data.
 * 
 * @param lookup - The lookup configuration object defining the API and parameters to use.
 * @param rootFormData - The root JSON form data used for parameter substitution.
 * @param path - A string path that can be used for data tracking or context.
 * 
 * @returns An object containing:
 *   - count: The fetched count of options (or null if unavailable).
 *   - loadingCount: Boolean flag indicating whether the count is currently loading.
 *   - errorLoadingCount: Any error encountered during loading.
 */
function useOptionsLookupCount({
  lookup,
  rootFormData,
  path
}: {
  lookup: LookupCountObject,
  rootFormData: JSONObject,
  path: string
}) {
  const { api: propApi, entity, jnx: apiResponseJnxExpr, params, options: fetchOptions } = lookup || {};

  const apiResponseJnx = useJnx(apiResponseJnxExpr);

  const paramsJnx = useMultipleJnx(params as MultipleJnxObject<string | number | boolean>);

  const paramsString = useMemo(() => computeParamString(rootFormData, params, paramsJnx), [rootFormData, params, paramsJnx]);

  const [count, loadingCount, errorLoadingCount] = useResourceLoader(
    async () => loadOptionsCount(propApi, apiResponseJnx, paramsString, fetchOptions, entity, path),
    [propApi, apiResponseJnx, paramsString, fetchOptions, entity, path]
  );

  return {
    count,
    loadingCount,
    errorLoadingCount,
  };
}


/**
 * Asynchronously fetches the count of options based on the lookup configuration and form data.
 * This function is a non-hook alternative for direct usage outside React components.
 * 
 * @param lookup - The lookup configuration object defining API and parameters.
 * @param rootFormData - The root JSON form data used for parameter substitution.
 * @param path - A string path for data tracking or context.
 * 
 * @returns A Promise resolving to the count of options (or null if unavailable).
 */
export async function lookupOptionsCount(
  lookup: LookupCountObject,
  rootFormData: JSONObject,
  path: string
) {
  const { api: propApi, entity, jnx: apiResponseJnxExpr, params, options: fetchOptions } = lookup || {};

  const apiResponseJnx = apiResponseJnxExpr ? new Jnx(apiResponseJnxExpr) : null;

  const paramsString = computeParamString(rootFormData, params);
  const count = await loadOptionsCount(propApi, apiResponseJnx, paramsString, fetchOptions, entity, path);

  return count;
}


/**
 * Computes the API URL and its module type based on the given API string and parameters.
 * 
 * The function supports these API string formats:
 * - "api:<endpoint>" or "entity:<entityName>" which are internal APIs/entities
 * - Full HTTP/HTTPS URLs for external calls
 * - Default "lookup" type for other API strings
 * 
 * @param api - The base API string or URL.
 * @param paramsString - The query parameter string to append.
 * 
 * @returns A tuple with:
 *   - module: The module category ('api', 'external', or 'lookup').
 *   - url: The full URL with parameters appended.
 */
function computeApiUrl(
  api: APIString,
  paramsString: string
): [string, string] {
  let module = null;
  let m = /^(api|entity):(.+)$/.exec(api);

  if (m) {
    module = 'api';
    api = m[1] === 'entity' ? `uwe-entities/${m[2]}/count` : `${m[2]}/count`;
  } else {
    m = /^https?:\/\/(.+)$/.exec(api);

    if (m) {
      module = 'external';
    } else {
      module = 'lookup';
    }
  }

  const delimiter = api.indexOf('?') > -1 ? '&' : '?';
  const url = `${api}${paramsString ? `${delimiter}${paramsString}` : ''}`;

  return [module, url];
}

/**
 * Internal helper function to load the count of options from the API.
 * It constructs the API URL, fetches the data, applies the Jnx transformation, and returns the count.
 * 
 * @param propApi - The API string or identifier to use.
 * @param apiResponseJnx - Optional Jnx instance to transform the response.
 * @param paramsString - The computed query parameter string.
 * @param fetchOptions - Options to configure the fetch request.
 * @param entity - Optional entity name to override the API path.
 * @param path - Optional data path for context or error handling.
 * 
 * @returns A Promise resolving to the numeric count or null.
 */
async function loadOptionsCount(
  propApi: APIString,
  apiResponseJnx: Jnx | null,
  paramsString: string,
  fetchOptions: FetchOptions,
  entity?: string | null,
  path?: string | null
): Promise<number | null> {
  const api = entity ? `entity:${entity}` : propApi;
  if (!api) return null;
  const [module, url] = computeApiUrl(api, paramsString);
  let data = await checkErrors(await fetchApi[module].get(url, fetchOptions));

  if (apiResponseJnx) {
    data = apiResponseJnx.eval(data, '', { dataPath: path, data });
  }

  return data as number | null;
}

export default useOptionsLookupCount;
